import { useParams, Link, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { ArrowLeft, AlertCircle, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useRecipeStore } from '@/stores/recipe-store'
import { InlineListEdit } from '@/components/recipe/inline-list-edit'
import IngredientScalerComponent from '@/components/recipe/ingredient-scaler'
import { UnifiedRecipeActions } from '@/components/recipe/unified-recipe-actions'
import { MealPlanningWidget } from '@/components/meal-planning/meal-planning-widget'
import { GetBringWidget } from '@/components/recipe/get-bring-widget'
import { useToast } from '@/components/ui/use-toast'
import { RecipeCookingMode } from '@/components/recipe/recipe-cooking-mode'
import { RecipeDetailHeader } from '@/components/recipe/recipe-detail-header'
import { RecipeInstructionsCard } from '@/components/recipe/recipe-instructions-card'
import { RecipeNotesCard } from '@/components/recipe/recipe-notes-card'
import { RecipeEquipmentCard } from '@/components/recipe/recipe-equipment-card'
import { RecipeDeleteDialog } from '@/components/recipe/recipe-delete-dialog'

type RecipeDetailState = {
  showDeleteDialog: boolean
  isInlineEditMode: boolean
  loadingRecipe: boolean
  isDeletingRecipe: boolean
  isCookingMode: boolean
  currentStep: number
  isSpeaking: boolean
}

type RecipeDetailAction =
  | { type: 'SET_DELETE_DIALOG'; payload: boolean }
  | { type: 'SET_INLINE_EDIT'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DELETING'; payload: boolean }
  | { type: 'SET_COOKING_MODE'; payload: boolean }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'SET_SPEAKING'; payload: boolean }

const initialState: RecipeDetailState = {
  showDeleteDialog: false,
  isInlineEditMode: false,
  loadingRecipe: false,
  isDeletingRecipe: false,
  isCookingMode: false,
  currentStep: 0,
  isSpeaking: false,
}

function reducer(state: RecipeDetailState, action: RecipeDetailAction): RecipeDetailState {
  switch (action.type) {
    case 'SET_DELETE_DIALOG':
      return { ...state, showDeleteDialog: action.payload }
    case 'SET_INLINE_EDIT':
      return { ...state, isInlineEditMode: action.payload }
    case 'SET_LOADING':
      return { ...state, loadingRecipe: action.payload }
    case 'SET_DELETING':
      return { ...state, isDeletingRecipe: action.payload }
    case 'SET_COOKING_MODE':
      return { ...state, isCookingMode: action.payload, currentStep: action.payload ? 0 : state.currentStep }
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload }
    case 'SET_SPEAKING':
      return { ...state, isSpeaking: action.payload }
    default:
      return state
  }
}

export default function RecipeDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { recipes, deleteRecipe, updateRecipe, loadRecipe } = useRecipeStore()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [selectedServings, setSelectedServings] = useState(4)
  const recipe = recipes.find((item) => item.id === id)

  useEffect(() => {
    if (!recipe && id && !state.loadingRecipe && !state.isDeletingRecipe) {
      dispatch({ type: 'SET_LOADING', payload: true })
      void loadRecipe(id).finally(() => {
        dispatch({ type: 'SET_LOADING', payload: false })
      })
    }
  }, [id, recipe, loadRecipe, state.isDeletingRecipe, state.loadingRecipe])

  const totalTime = useMemo(
    () => (recipe ? (recipe.prep_time || 0) + (recipe.cook_time || 0) : 0),
    [recipe]
  )

  useEffect(() => {
    if (!recipe) {
      return
    }

    setSelectedServings(recipe.servings || 4)
  }, [recipe?.id, recipe?.servings])

  const handleDelete = async () => {
    if (!recipe) {
      return
    }

    dispatch({ type: 'SET_DELETING', payload: true })
    const success = await deleteRecipe(recipe.id)
    if (success) {
      navigate('/recepten')
      toast({
        title: 'Recept verwijderd',
        description: 'Het recept is succesvol verwijderd.',
      })
      return
    }

    dispatch({ type: 'SET_DELETING', payload: false })
  }

  const handleInlineUpdate = async (field: string, value: unknown) => {
    if (!recipe) {
      return false
    }

    const updated = await updateRecipe(recipe.id, { [field]: value } as Record<string, unknown>)
    if (updated) {
      toast({
        title: 'Bijgewerkt',
        description: 'Het recept is succesvol bijgewerkt.',
      })
      return true
    }

    toast({
      title: 'Fout bij bijwerken',
      description: 'Het recept kon niet worden bijgewerkt.',
      variant: 'destructive',
    })
    return false
  }

  const handleTogglePublicStatus = async () => {
    if (!recipe) {
      return
    }

    const updated = await updateRecipe(recipe.id, { is_public: !recipe.is_public })
    if (updated) {
      toast({
        title: updated.is_public ? 'Recept is nu publiek' : 'Recept is nu afgeschermd',
        description: updated.is_public
          ? 'Andere gebruikers kunnen dit recept nu vinden in Zoeken en gebruiken voor Bring.'
          : 'Dit recept is niet langer publiek zichtbaar.',
      })
      return
    }

    toast({
      title: 'Status niet bijgewerkt',
      description: 'De zichtbaarheid van dit recept kon niet worden aangepast.',
      variant: 'destructive',
    })
  }

  const handlePrint = () => window.print()

  const speakText = (text: string) => {
    if (!('speechSynthesis' in window)) {
      return
    }

    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'nl-NL'
    utterance.onend = () => dispatch({ type: 'SET_SPEAKING', payload: false })
    dispatch({ type: 'SET_SPEAKING', payload: true })
    window.speechSynthesis.speak(utterance)
  }

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()
      dispatch({ type: 'SET_SPEAKING', payload: false })
    }
  }

  if (!recipe) {
    return (
      <div className="container py-8">
        <div className="mb-6">
          <Button variant="outline" asChild>
            <Link to="/recepten">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug naar Recepten
            </Link>
          </Button>
        </div>

        <div className="text-center py-12">
          {state.loadingRecipe ? (
            <>
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-green-200 border-t-green-600 mx-auto mb-4"></div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Recept laden...</h1>
              <p className="text-gray-600">Even geduld, we halen het recept op.</p>
            </>
          ) : (
            <>
              <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Recept niet gevonden</h1>
              <p className="text-gray-600">Het recept dat je zoekt bestaat niet of is verwijderd.</p>
            </>
          )}
        </div>
      </div>
    )
  }

  if (state.isCookingMode) {
    return (
      <RecipeCookingMode
        currentStep={state.currentStep}
        instructions={recipe.instructions}
        isSpeaking={state.isSpeaking}
        onBack={() => dispatch({ type: 'SET_COOKING_MODE', payload: false })}
        onPrevious={() => dispatch({ type: 'SET_CURRENT_STEP', payload: Math.max(0, state.currentStep - 1) })}
        onNext={() =>
          dispatch({
            type: 'SET_CURRENT_STEP',
            payload: Math.min(recipe.instructions.length - 1, state.currentStep + 1),
          })
        }
        onToggleSpeaking={() =>
          state.isSpeaking ? stopSpeaking() : speakText(recipe.instructions[state.currentStep])
        }
      />
    )
  }

  return (
    <div className="container py-6 sm:py-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="outline" asChild className="w-full sm:w-auto">
          <Link to="/recepten">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Terug naar Recepten
          </Link>
        </Button>

        <Button variant="outline" onClick={handlePrint} className="w-full sm:w-auto">
          <Printer className="h-4 w-4 mr-2" />
          Print
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <RecipeDetailHeader
            recipe={recipe}
            isInlineEditMode={state.isInlineEditMode}
            totalTime={totalTime}
            onStartCookingMode={() => dispatch({ type: 'SET_COOKING_MODE', payload: true })}
            onInlineUpdate={handleInlineUpdate}
          />

          {state.isInlineEditMode ? (
            <Card>
              <CardHeader>
                <CardTitle>Ingrediënten</CardTitle>
              </CardHeader>
              <CardContent>
                <InlineListEdit
                  items={recipe.ingredients}
                  onSave={(items) => handleInlineUpdate('ingredients', items)}
                  placeholder="Voeg ingrediënt toe..."
                  addButtonText="Ingrediënt toevoegen"
                  itemType="ingredient"
                />
              </CardContent>
            </Card>
          ) : (
            <IngredientScalerComponent
              ingredients={recipe.ingredients}
              originalServings={recipe.servings || 4}
              onServingsChange={(servings) => setSelectedServings(servings)}
              showCard
            />
          )}

          <RecipeInstructionsCard
            instructions={recipe.instructions}
            isInlineEditMode={state.isInlineEditMode}
            onInlineUpdate={handleInlineUpdate}
          />

          <RecipeNotesCard
            notes={recipe.notes}
            isInlineEditMode={state.isInlineEditMode}
            onInlineUpdate={handleInlineUpdate}
          />
        </div>

        <div className="space-y-6">
          <UnifiedRecipeActions
            recipe={recipe}
            isInlineEditMode={state.isInlineEditMode}
            onToggleInlineEdit={() => dispatch({ type: 'SET_INLINE_EDIT', payload: !state.isInlineEditMode })}
            onDeleteRecipe={() => dispatch({ type: 'SET_DELETE_DIALOG', payload: true })}
            onTogglePublicStatus={handleTogglePublicStatus}
          />

          <MealPlanningWidget recipe={recipe} />
          <GetBringWidget recipe={recipe} servings={selectedServings} />

          <RecipeEquipmentCard equipment={recipe.equipment} />
        </div>
      </div>

      {state.showDeleteDialog && (
        <RecipeDeleteDialog
          recipeTitle={recipe.title}
          onCancel={() => dispatch({ type: 'SET_DELETE_DIALOG', payload: false })}
          onConfirm={handleDelete}
        />
      )}
    </div>
  )
}
